'use client'

// This file MUST be imported before any components that use AppKit hooks
// It initializes the AppKit modal at module load time

import { createAppKit } from '@reown/appkit/react'
import { wagmiAdapter, projectId, metadata, supportedChains } from './wagmi-config'
import { siweConfig } from './siwe-config'

// Multi-chain adapters
import { SolanaAdapter } from '@reown/appkit-adapter-solana/react'
import { TonAdapter } from '@reown/appkit-adapter-ton'
import { TronAdapter } from '@reown/appkit-adapter-tron'

// Import AppKitNetwork type, defineChain, and TON networks
import { defineChain, ton, tonTestnet } from '@reown/appkit/networks'
import type { AppKitNetwork } from '@reown/appkit/networks'

// ============================================
// GLOBAL ERROR HANDLER FOR WALLET ERRORS
// Catches "MetaMask extension not found" and similar errors
// that are thrown by Wagmi connectors when extension isn't installed
// ============================================
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason
        // Check if this is a MetaMask extension not found error
        if (reason?.message?.includes('MetaMask extension not found') ||
            reason?.message?.includes('MetaMask is not installed') ||
            (reason?.name === 'n6' && reason?.message?.includes('Failed to connect to MetaMask'))) {
            // Prevent the error from showing in console
            event.preventDefault()
            console.warn('[AppKit] MetaMask not installed - user should install extension')
        }
    })
}

// Determine if testnet mode
const isTestnet = process.env.NEXT_PUBLIC_NETWORK_MODE === 'testnet'

// ============================================
// TRON NETWORK DEFINITIONS
// ============================================
// Tron Mainnet - chainId from WalletConnect CAIP standard
const tronMainnet = defineChain({
    id: '0x2b6653dc',
    name: 'TRON',
    nativeCurrency: { name: 'TRON', symbol: 'TRX', decimals: 6 },
    rpcUrls: {
        default: { http: [process.env.NEXT_PUBLIC_TRON_RPC || 'https://api.trongrid.io'] }
    },
    blockExplorers: { default: { name: 'TronScan', url: process.env.NEXT_PUBLIC_TRON_EXPLORER || 'https://tronscan.org' } },
    testnet: false,
    chainNamespace: 'tron' as any,
    caipNetworkId: 'tron:0x2b6653dc' as any
})

// Tron Nile Testnet
const tronTestnet = defineChain({
    id: '0xcd8690dc',
    name: 'TRON Nile Testnet',
    nativeCurrency: { name: 'TRON', symbol: 'TRX', decimals: 6 },
    rpcUrls: {
        default: { http: [process.env.NEXT_PUBLIC_TRON_TESTNET_RPC || 'https://nile.trongrid.io'] }
    },
    blockExplorers: { default: { name: 'Nile TronScan', url: process.env.NEXT_PUBLIC_TRON_TESTNET_EXPLORER || 'https://nile.tronscan.org' } },
    testnet: true,
    chainNamespace: 'tron' as any,
    caipNetworkId: 'tron:0xcd8690dc' as any
})

// Active Tron network based on mode
const activeTronNetwork = isTestnet ? tronTestnet : tronMainnet

// Define Solana RPC endpoints (direct endpoints, not WalletConnect proxy)
const SOLANA_RPC = {
    mainnet: process.env.NEXT_PUBLIC_SOLANA_RPC || 'https://api.mainnet-beta.solana.com',
    testnet: process.env.NEXT_PUBLIC_SOLANA_TESTNET_RPC || 'https://api.testnet.solana.com',
    devnet: process.env.NEXT_PUBLIC_SOLANA_DEVNET_RPC || 'https://api.devnet.solana.com',
}

// Create FULLY CUSTOM Solana network to avoid WalletConnect RPC proxy issues
// The "Endpoint URL must start with http:" error occurs when AppKit can't resolve the RPC
const customSolanaNetwork: AppKitNetwork = {
    id: isTestnet ? '4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z' : '5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp',
    name: isTestnet ? 'Solana Testnet' : 'Solana',
    nativeCurrency: {
        name: 'Solana',
        symbol: 'SOL',
        decimals: 9,
    },
    rpcUrls: {
        default: {
            http: [isTestnet ? SOLANA_RPC.testnet : SOLANA_RPC.mainnet],
        },
    },
    blockExplorers: {
        default: {
            name: 'Solscan',
            url: isTestnet
                ? (process.env.NEXT_PUBLIC_SOLANA_TESTNET_EXPLORER || 'https://solscan.io/?cluster=testnet')
                : (process.env.NEXT_PUBLIC_SOLANA_EXPLORER || 'https://solscan.io'),
        },
    },
    testnet: isTestnet,
    chainNamespace: 'solana',
    caipNetworkId: isTestnet
        ? 'solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z'
        : 'solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp',
}

// Create Solana adapter
const solanaAdapter = new SolanaAdapter()

// Create TON adapter
const tonAdapter = new TonAdapter({ projectId: projectId! })

// Create Tron adapter (official @reown/appkit-adapter-tron — discovers injected Tron wallets & WC Tron sessions)
const tronAdapter = new TronAdapter()

// Active TON network based on mode
const activeTonNetwork = isTestnet ? tonTestnet : ton

// Build networks array - EVM first, then Solana, then Tron, then TON
const evmNetworks = supportedChains as unknown as AppKitNetwork[]
const allNetworks: [AppKitNetwork, ...AppKitNetwork[]] = [
    evmNetworks[0],
    ...evmNetworks.slice(1),
    customSolanaNetwork,
    activeTronNetwork, // Add Tron network
    activeTonNetwork,  // Add TON network
]

// Debug: Log networks on client side
if (typeof window !== 'undefined') {
    console.log('[AppKit] Configured Solana network:', {
        id: customSolanaNetwork.id,
        name: customSolanaNetwork.name,
        rpcUrl: customSolanaNetwork.rpcUrls?.default?.http?.[0],
        caipNetworkId: customSolanaNetwork.caipNetworkId,
    })

    console.log('[AppKit] Configured Tron network:', {
        id: activeTronNetwork.id,
        name: activeTronNetwork.name,
        rpcUrl: activeTronNetwork.rpcUrls?.default?.http?.[0],
        caipNetworkId: activeTronNetwork.caipNetworkId,
    })

    console.log('[AppKit] Configured TON network:', {
        id: activeTonNetwork.id,
        name: activeTonNetwork.name,
        caipNetworkId: (activeTonNetwork as any).caipNetworkId,
    })

    // Clear any stale session data that might have invalid RPC
    // This prevents the "Endpoint URL must start with http" error on reconnection
    try {
        const keysToCheck = Object.keys(localStorage || {})
        keysToCheck.forEach(key => {
            // Clear stale AppKit network cache that might have invalid RPC URLs
            if (key.includes('@appkit') && (key.includes('solana') || key.includes('tron'))) {
                console.log('[AppKit] Clearing potentially stale cache:', key)
                localStorage.removeItem(key)
            }
        })
    } catch (e) {
        // Ignore localStorage errors
    }
}

// Initialize AppKit - This MUST happen before any hooks are used
// This will run on both server and client when the module is loaded
let appKitInstance: ReturnType<typeof createAppKit> | null = null

if (projectId) {
    try {
        appKitInstance = createAppKit({
            adapters: [wagmiAdapter, solanaAdapter, tonAdapter, tronAdapter],
            projectId,
            networks: allNetworks,
            metadata,
            siweConfig,
            themeMode: 'dark',
            themeVariables: {
                '--w3m-accent': '#34d399',
                '--w3m-color-mix': '#10b981',
                '--w3m-color-mix-strength': 20,
                '--w3m-border-radius-master': '12px',
                '--w3m-font-family': 'Geist, sans-serif',
                '--w3m-z-index': 1000,
            },
            features: {
                analytics: false,
                // Show wallet connection methods first, with WalletConnect QR at top
                connectMethodsOrder: ['wallet'] as any,
            },
            // Always show wallet list even without browser extensions
            enableWallets: true,
            // Show all wallets including WalletConnect for non-EVM chains
            allWallets: 'SHOW' as any,
            // Always show "Get a Wallet" guide for users without wallets
            enableWalletGuide: true,
            // Enable injected wallet detection to prevent "MetaMask extension not found" errors
            // AppKit will show install links instead of throwing errors
            enableWalletConnect: true,
        })

        if (typeof window !== 'undefined') {
            console.log('[AppKit] Initialized successfully with project ID:', projectId?.substring(0, 8) + '...')
        }
    } catch (error) {
        console.error('[AppKit] Failed to initialize:', error)
    }
}

// Export for use in other modules
export { appKitInstance, allNetworks, solanaAdapter, activeTronNetwork, tonAdapter, activeTonNetwork, tronAdapter, customSolanaNetwork }
